import { formatDate, formatMoney, useTranslation } from "@roastery/i18n";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@roastery/ui";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { RotateCcwIcon } from "lucide-react";
import { OrderStatusBadge } from "../components/order-status-badge";
import { type PortalOrder, usePortalOrders } from "../lib/portal-api";
import { useIsWide } from "../lib/use-media-query";
import { useReorder } from "../lib/use-reorder";
import { usePortalFormatSettings } from "../lib/use-portal-format";

export const Route = createFileRoute("/_authed/orders/")({
  component: OrdersPage,
});

/**
 * What this customer has ordered (US-10.4).
 *
 * <p>Newest first and nothing to filter: a cafe places one order a week, so a search box would be
 * furniture. The one thing they do come here to do is order the same thing again, which is why that
 * button is on every row rather than only on the order itself.
 */
function OrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const settings = usePortalFormatSettings();
  const orders = usePortalOrders();
  const wide = useIsWide();
  const reorder = useReorder();

  if (orders.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="portal-orders-loading">
        {t("app.loading")}
      </p>
    );
  }

  const reorderButton = (order: PortalOrder) => (
    <Button
      variant="outline"
      size="sm"
      data-testid={`portal-reorder-${order.orderNumber}`}
      onClick={() => reorder(order.lines ?? [])}
    >
      <RotateCcwIcon aria-hidden />
      {t("portal.orders.reorder")}
    </Button>
  );

  const rows = (orders.data?.pages ?? []).flatMap((page) => page.content ?? []);
  const total = orders.data?.pages?.[0]?.totalElements ?? rows.length;
  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-4" data-testid="portal-orders-empty">
        <PageHeader title={t("portal.orders.title")} />
        <Card>
          <CardHeader>
            <CardDescription>{t("portal.orders.empty")}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button data-testid="portal-back-to-catalog" onClick={() => void navigate({ to: "/" })}>
              {t("portal.cart.placed.backToCatalog")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="portal-orders">
      <div>
        <PageHeader title={t("portal.orders.title")} />
        <p className="text-sm text-muted-foreground">{t("portal.orders.description")}</p>
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-6">
          {wide ? (
            <Table data-testid="portal-orders-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("portal.orders.columns.number")}</TableHead>
                  <TableHead>{t("portal.orders.columns.placed")}</TableHead>
                  <TableHead>{t("portal.orders.columns.delivery")}</TableHead>
                  <TableHead>{t("portal.orders.columns.status")}</TableHead>
                  <TableHead>{t("portal.orders.columns.tracking")}</TableHead>
                  <TableHead className="text-right">{t("portal.orders.columns.total")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((order) => (
                  <TableRow key={order.id} data-testid={`portal-order-${order.orderNumber}`}>
                    <TableCell className="font-mono font-medium">
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: order.id ?? "" }}
                        className="underline-offset-2 hover:underline"
                        data-testid={`portal-order-link-${order.orderNumber}`}
                      >
                        {order.orderNumber}
                      </Link>
                    </TableCell>
                    <TableCell>{order.placedAt ? formatDate(order.placedAt, settings) : "—"}</TableCell>
                    <TableCell>
                      {order.requestedDeliveryDate ? formatDate(order.requestedDeliveryDate, settings) : "—"}
                    </TableCell>
                    <TableCell>
                      <OrderStatusBadge
                        status={order.status}
                        testId={`portal-order-status-${order.orderNumber}`}
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {trackingOf(order) || "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatMoney(Number(order.totalGross ?? 0), settings)}
                    </TableCell>
                    <TableCell className="text-right">
                      {reorderButton(order)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            // A phone gets two lines per order — number and status, then date and amount — and the
            // reorder button under them; seven columns were wider than the screen.
            <ul className="flex flex-col gap-3" data-testid="portal-orders-table">
              {rows.map((order) => (
                <li
                  key={order.id}
                  className="flex flex-col gap-2 rounded-md border border-border p-3"
                  data-testid={`portal-order-${order.orderNumber}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: order.id ?? "" }}
                      className="font-mono font-medium whitespace-nowrap underline-offset-2 hover:underline"
                      data-testid={`portal-order-link-${order.orderNumber}`}
                    >
                      {order.orderNumber}
                    </Link>
                    <OrderStatusBadge status={order.status} testId={`portal-order-status-${order.orderNumber}`} />
                  </div>
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">
                      {order.placedAt ? formatDate(order.placedAt, settings) : "—"}
                    </span>
                    <span className="tabular-nums font-medium">
                      {formatMoney(Number(order.totalGross ?? 0), settings)}
                    </span>
                  </div>
                  {trackingOf(order) ? (
                    <div className="text-xs text-muted-foreground">{trackingOf(order)}</div>
                  ) : null}
                  {reorderButton(order)}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
        {orders.hasNextPage ? (
          <CardFooter className="justify-center">
            <Button
              variant="outline"
              data-testid="portal-orders-more"
              disabled={orders.isFetchingNextPage}
              onClick={() => void orders.fetchNextPage()}
            >
              {orders.isFetchingNextPage
                ? t("app.loading")
                : t("portal.orders.showMore", { shown: rows.length, total })}
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </div>
  );
}

/** Carrier and tracking number of every parcel, or an empty string while nothing has left. */
function trackingOf(order: PortalOrder): string {
  return (order.shipments ?? [])
    .map((parcel) => [parcel.carrier, parcel.trackingNumber].filter(Boolean).join(" "))
    .join(", ");
}
